import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { SlideProps } from './types';
import { createComponentLogger } from './logger';

// VideoSlide: Plays video and completes after video ends + additional duration
const VideoSlide: React.FC<SlideProps> = ({
  source,
  isActive,
  defaultDuration,
  onComplete,
  onError
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const completionTimerRef = useRef<number | null>(null);
  const failoverTimerRef = useRef<number | null>(null);
  const networkCheckIntervalRef = useRef<number | null>(null);
  const hasCompletedRef = useRef(false);

  // Create logger instance once
  const log = useMemo(() => createComponentLogger('VideoSlide'), []);

  // Stabilize callbacks to prevent effect re-runs and stale closures
  const handleComplete = useCallback(() => {
    if (hasCompletedRef.current) {
      log('⚠️  handleComplete called but already completed, ignoring');
      return;
    }
    hasCompletedRef.current = true;
    log('✅ VideoSlide completed - calling onComplete()');
    onComplete();
  }, [onComplete, log]);

  const handleVideoError = useCallback((error: Error | MediaError) => {
    log('❌ VideoSlide error', {
      errorType: error instanceof MediaError ? 'MediaError' : 'Error',
      code: error instanceof MediaError ? error.code : undefined,
      message: error.message || 'Unknown error'
    });
    onError(error);
    handleComplete();
  }, [onError, handleComplete, log]);

  // Clear all timers utility
  const clearAllTimers = useCallback(() => {
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    if (failoverTimerRef.current) {
      clearTimeout(failoverTimerRef.current);
      failoverTimerRef.current = null;
    }
    if (networkCheckIntervalRef.current) {
      clearInterval(networkCheckIntervalRef.current);
      networkCheckIntervalRef.current = null;
    }
  }, []);

  // Main video lifecycle effect
  useEffect(() => {
    log('🎬 VideoSlide effect triggered', {
      isActive,
      hasVideoRef: !!videoRef.current,
      sourceUrl: source.url,
      defaultDuration,
      additionalDuration: source.duration
    });

    if (!isActive) {
      log('⏸️  Not active, skipping initialization');
      return;
    }

    if (!videoRef.current) {
      log('⚠️  No video ref available, skipping initialization');
      return;
    }

    const video = videoRef.current;
    hasCompletedRef.current = false;

    log('🚀 Initializing video playback', {
      videoSrc: video.src,
      currentTime: video.currentTime,
      readyState: video.readyState,
      networkState: video.networkState
    });

    // Event handlers
    const handleLoadedMetadata = () => {
      log('📊 Video metadata loaded', {
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState
      });

      // Clear failover since video loaded successfully
      if (failoverTimerRef.current) {
        clearTimeout(failoverTimerRef.current);
        failoverTimerRef.current = null;
        log('✓ Cleared failover timer (metadata loaded)');
      }
    };

    const handleLoadStart = () => {
      log('📥 Video load started');
    };

    const handleLoadedData = () => {
      log('📦 Video data loaded', { readyState: video.readyState });
    };

    const handleCanPlay = () => {
      log('▶️  Video can play', { readyState: video.readyState });
    };

    const handleCanPlayThrough = () => {
      log('▶️▶️  Video can play through', { readyState: video.readyState });
    };

    const handlePlaying = () => {
      log('🎥 Video playing', {
        currentTime: video.currentTime,
        duration: video.duration,
        paused: video.paused
      });

      // Clear failover timer once playing starts
      if (failoverTimerRef.current) {
        clearTimeout(failoverTimerRef.current);
        failoverTimerRef.current = null;
        log('✓ Cleared failover timer (playing started)');
      }
    };

    const handlePause = () => {
      log('⏸️  Video paused', {
        currentTime: video.currentTime,
        duration: video.duration
      });
    };

    const handleSeeking = () => {
      log('⏩ Video seeking', { currentTime: video.currentTime });
    };

    const handleSeeked = () => {
      log('✓ Video seeked', { currentTime: video.currentTime });
    };

    const handleTimeUpdate = () => {
      // Log less frequently to avoid spam, only every 2 seconds
      const currentTime = video.currentTime;
      if (Math.floor(currentTime) % 2 === 0 && currentTime % 1 < 0.1) {
        log('⏱️  Time update', {
          currentTime: currentTime.toFixed(2),
          duration: video.duration?.toFixed(2),
          percentComplete: ((currentTime / video.duration) * 100).toFixed(1) + '%'
        });
      }
    };

    const handleEnded = () => {
      log('🏁 Video ended', {
        currentTime: video.currentTime,
        duration: video.duration
      });

      // Schedule completion after additional duration
      const additionalDuration = source.duration ?? defaultDuration;
      log(`⏳ Scheduling completion in ${additionalDuration}s`);

      completionTimerRef.current = window.setTimeout(() => {
        log('⏰ Completion timer fired');
        handleComplete();
      }, additionalDuration * 1000);
    };

    const handleError = () => {
      const error = video.error || new Error('Unknown video error');
      log('💥 Video error event', {
        errorCode: video.error?.code,
        networkState: video.networkState,
        readyState: video.readyState
      });
      handleVideoError(error);
    };

    const handleStalled = () => {
      log('⚠️  Video playback stalled', {
        currentTime: video.currentTime,
        networkState: video.networkState,
        readyState: video.readyState
      });
    };

    const handleWaiting = () => {
      log('⏳ Video buffering', {
        currentTime: video.currentTime,
        networkState: video.networkState,
        readyState: video.readyState
      });
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const percentBuffered = ((bufferedEnd / video.duration) * 100).toFixed(1);
        log('📊 Video loading progress', {
          buffered: bufferedEnd.toFixed(2),
          duration: video.duration?.toFixed(2),
          percentBuffered: percentBuffered + '%'
        });
      }
    };

    const handleSuspend = () => {
      log('💤 Video loading suspended');
    };

    const handleAbort = () => {
      log('🛑 Video loading aborted');
    };

    // Network state monitoring (check periodically for edge cases not caught by events)
    log('⚙️  Starting network monitoring interval (1s)');
    networkCheckIntervalRef.current = window.setInterval(() => {
      if (!video) return;

      // Check for network errors (NETWORK_NO_SOURCE = 3)
      if (video.networkState === 3) {
        log('🌐 Network error detected: NETWORK_NO_SOURCE', {
          networkState: video.networkState,
          readyState: video.readyState
        });
        handleVideoError(new Error('Video network error: no source'));
        if (networkCheckIntervalRef.current) {
          clearInterval(networkCheckIntervalRef.current);
          networkCheckIntervalRef.current = null;
        }
        return;
      }

      // Check for error state (backup check in case error event doesn't fire)
      if (video.error) {
        log('🌐 Network check detected video error', {
          errorCode: video.error.code,
          networkState: video.networkState
        });
        handleVideoError(video.error);
        if (networkCheckIntervalRef.current) {
          clearInterval(networkCheckIntervalRef.current);
          networkCheckIntervalRef.current = null;
        }
      }
    }, 1000); // Check every second instead of 250ms

    // Failover: if video doesn't start playing within timeout
    log(`⏲️  Setting failover timeout (${defaultDuration}s)`);
    failoverTimerRef.current = window.setTimeout(() => {
      log('⏰ Failover timeout fired - video failed to start');
      handleVideoError(new Error('Video loading timeout'));
    }, defaultDuration * 1000);

    // Attach event listeners
    log('🎧 Attaching event listeners');
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('stalled', handleStalled);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('suspend', handleSuspend);
    video.addEventListener('abort', handleAbort);

    // Start playback
    log('▶️  Resetting video to start and calling play()');
    video.currentTime = 0;
    const playPromise = video.play();

    playPromise
      .then(() => {
        log('✓ Play promise resolved successfully');
      })
      .catch((err: unknown) => {
        // Ignore AbortError (usually from pause() during cleanup or slide transition)
        if (err instanceof DOMException && err.name === 'AbortError') {
          log('⚠️  Video play aborted (likely slide transition)', {
            errorName: err.name
          });
          return;
        }

        log('❌ Play promise rejected', {
          error: err instanceof Error ? err.message : String(err),
          errorType: err instanceof Error ? err.constructor.name : typeof err
        });
        handleVideoError(err instanceof Error ? err : new Error(String(err)));
      });

    // Cleanup
    return () => {
      log('🧹 Cleanup function called - removing listeners and stopping playback');

      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('stalled', handleStalled);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('suspend', handleSuspend);
      video.removeEventListener('abort', handleAbort);

      clearAllTimers();

      // Pause video if it's playing (synchronous, no promise needed)
      if (!video.paused) {
        log('⏸️  Pausing video during cleanup');
        video.pause();
      }

      log('✅ Cleanup complete');
    };
  }, [isActive, source.duration, source.url, defaultDuration, handleComplete, handleVideoError, clearAllTimers, log]);

  const isMuted = source.mute ?? true;

  return (
    <video
      ref={videoRef}
      src={source.url}
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      muted={isMuted}
      playsInline
      preload="auto"
    />
  );
};

export default VideoSlide;
